import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import FullCalendar, { formatDate } from "@fullcalendar/react"; // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import PageWrapper from "../components/layout/PageWrapper";
import { useWizard, Wizard } from "react-use-wizard";
import useExistingUser from "../controllers/appointments/useExistingUser";
import Profile from "../models/domain/Profile";
import useAppointmentSlots from "../controllers/appointments/useAppointmentSlots";
import { Alert, Autocomplete } from "@material-ui/lab";
import AppointmentDateSlot from "../models/domain/AppointmentDateSlot";
import PatientSearchQuery from "../models/domain/PatientSearchQuery";
import { useSnackbarContext } from "../contexts/Snackbar";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import HttpClient from "../lib/HttpClient";
import AppointmentDate from "../models/domain/AppointmentDate";
import AppointmentSummary from "../components/appointments/AppointmentSummary";
import utils from "../Utils";
import AppointmentInfo from "../models/domain/AppointmentInfo";
import CancelAppointmentDialog from "../components/appointments/CancelAppointmentDialog";
import {
  Button,
  Chip,
  Container,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import ProfileMapper from "../mappers/ProfileMapper";
import moment from "moment";
import { MdOutlineSearch } from "react-icons/md";
import UploadReportDialog from "../components/reports/UploadReportDialog";
import Reports from "../components/reports/Reports";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import SpaceMetadata from "../models/domain/SpaceMetadata";
import PreferredDoctors from "../components/appointments/PreferredDoctors";
import UpcomingUserAppointments from "../models/domain/UpcomingUserAppointment";
import useUpcomingUserVisit from "../controllers/appointments/useUpcomingUserVisit";
import UpcomingUserWaitlist from "../models/domain/UpcomingUserWaitlist";
import useClinicTherapies from "../controllers/appointments/useClinicTherapies";
import Therapy from "../models/domain/Therapy";
import useAppointmentList from "../controllers/appointments/useAppointmentList";
import AppointmentCard from "../components/appointments/AppointmentCard";
import PatientRelationDialog from "../components/appointments/PatientRelationDialog";

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;
  //https://github.com/mui/material-ui/issues/21015#issuecomment-663208786
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Container>
          <Box>{children}</Box>
        </Container>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
}));

export default function AppHelpPage() {
  const receptionistProfile = utils.retrieveUserProfile();
  let [searchParams, setSearchParams] = useSearchParams();
  let [mainUserProfile, setMainUserProfile] = useState<Profile>();
  let [multipleProfiles, setMultipleProfiles] = useState<Profile[]>([]);
  let [selectedDate, setSelectedDate] = useState<string>("");
  let [oldAppointmentId, setOldAppointmentId] = useState<string>("");
  let [selectedTimeSlot, setSelectedTimeSlot] = useState<AppointmentDateSlot>();
  let [selectedEndTimeSlot, setSelectedEndTimeSlot] =
    useState<AppointmentDateSlot>();
  let [selectedTherapy, setSelectedTherapy] = useState<Therapy | null>(null);
  let [therapyBookingNote, setTherapyBookingNote] = useState<string>("");

  let [userType, setUserType] = useState<string>("existing");

  let [isAppointmentTab, setIsAppointmentTab] = useState(true);
  let [showUploadDialog, setShowUploadDialog] = useState(false);

  const [sessionBookings, setSessionBookings] = useState<
    {
      date: string;
      startTime: string;
      endTime?: string;
      patientName: string;
      doctorName: string;
      clinicName: string;
      therapyName?: string;
    }[]
  >([]);

  const [patientSearchParameter, setPatientSearchParameter] =
    useState<PatientSearchQuery>();

  let [showPatientRelationDialog, setShowPatientRelationDialog] =
    useState(false);
  let [patientRelationType, setPatientRelationType] = useState<
    "NEW" | "EXISTING_MIGRATE"
  >("NEW");
  type PendingSubmitFn = (relationType: "NEW" | "EXISTING_MIGRATE") => void;

  let [pendingNewPatientSubmit, setPendingNewPatientSubmit] =
    useState<PendingSubmitFn | null>(null);
  let [pendingDoctorName, setPendingDoctorName] = useState<string>("");

  const classes = useStyles();
  const [value, setValue] = React.useState(0);

  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);
  };

  const userProfile = utils.retrieveUserProfile();

  const isScheduledBySelf = (appointmentAtClinic: string) => {
    return userProfile.clinicName === appointmentAtClinic;
  };

  const resetBookingState = () => {
    setSessionBookings([]);
    setSelectedDate("");
    setSelectedTimeSlot(undefined);
    setSelectedEndTimeSlot(undefined);
    setSelectedTherapy(null);
    setTherapyBookingNote("");
  };

  useEffect(() => {
    setMainUserProfile(undefined);
  }, [userType]);

  const Step1 = () => {
    const { nextStep, goToStep } = useWizard();
    const { setSnackbar } = useSnackbarContext();

    const [receptionistProfile, setReceptionistProfile] = useState(
      utils.retrieveUserProfile(),
    );

    //set prefered doctor default to first doctor from receptionist profile associated health providers array
    const [preferedDoctor, setPreferedDoctor] = useState(
      receptionistProfile.associatedHealthCareProviders.find(
        (healthcareProvider) =>
          healthcareProvider.providerType === "Doctor" ||
          healthcareProvider.providerType === "Dietitian",
      ) || utils.defaultDoctor,
    );

    // this section is to handle the scenario when receptionist clicks on reschedule from appointment summary page. we are passing doctor id and patient id as query params and setting the prefered doctor based on that. this will ensure that when receptionist clicks on reschedule, the doctor selected in step 1 will be the same as the doctor of the appointment which is being rescheduled.
    useEffect(() => {
      const step = searchParams.get("w");
      const did = searchParams.get("did");

      if (did) {
        const matchedById =
          receptionistProfile.associatedHealthCareProviders.find(
            (healthcareProvider) =>
              String(healthcareProvider.id) === String(did),
          );

        if (matchedById) {
          setPreferedDoctor(matchedById);

          localStorage.setItem(
            "preferredDoctorForAppointmentBooking",
            JSON.stringify(matchedById),
          );
        }
      }
      if (step) {
        goToStep(parseInt(step));
      }
    }, [searchParams, receptionistProfile]);

    // @ts-ignore
    return (
      <div className="first_step">
        <div className="w-100 d-flex align-items-center justify-content-end ">
          <span style={{ height: 24 }} />{" "}
          {/* spacer to match Clear / New Patient height */}
        </div>
        <Formik
          initialValues={{
            newEmail: "",
            newMobile: "",
            firstName: "",
            middleName: "",
            lastName: "",
            mobile: "",
            practoId: "",
            searchName: "",
          }}
          onSubmit={(values, { setSubmitting }) => {
            if (userType === "new") {
              setPendingDoctorName(
                preferedDoctor?.name || receptionistProfile?.doctorName || "",
              );
              setPendingNewPatientSubmit(
                () => (relationType: "NEW" | "EXISTING_MIGRATE") => {
                  HttpClient.performPost<any>(`/api/v1/users`, {
                    firstName: values.firstName,
                    middleName: values.middleName,
                    lastName: values.lastName,
                    mobile: String(values.newMobile),
                    email: values.newEmail,
                    healthProviderId: preferedDoctor.id,
                    clinicId: receptionistProfile.clinicId,
                    patientOnboardType: relationType,
                  })
                    .then((response) => {
                      if (!response?.body?.status?.success) {
                        setSnackbar({
                          message: response?.body?.status?.message,
                          severity: "error",
                        });
                      } else {
                        setSnackbar({
                          message: "New User successfully!",
                          severity: "success",
                        });
                        setMainUserProfile({
                          name: `${values.firstName} ${values.middleName} ${values.lastName}`,
                          clinic_patient_id:
                            response?.body?.appId ?? response?.body?.patientId,
                          mobile: values.newMobile,
                          email: values.newEmail,
                          appId:
                            response?.body?.appId ?? response?.body?.patientId,
                          doctorId: preferedDoctor.id,
                        });
                        nextStep();
                      }
                    })
                    .catch((e) => {
                      console.log(e);
                    })
                    .finally(() => {
                      setSubmitting(false);
                    });
                },
              );
              setShowPatientRelationDialog(true);
              setSubmitting(false);
              return; // 👈 stop here, wait for dialog confirm
            } else {
              let getExistingUserUrl = ``;
              if (!values.mobile && !values.practoId && !values.searchName) {
                setSnackbar({
                  message:
                    multipleProfiles?.length > 1
                      ? "Please select one of the patients from the list"
                      : "Please enter mobile number, name or patient id",
                  severity: "error",
                });
                return;
              }
              if (values.practoId) {
                getExistingUserUrl = `/api/v1/users/search?patientId=${values.practoId}`;
                setPatientSearchParameter({
                  type: "appId",
                  value: values.practoId,
                });
              } else if (values.mobile) {
                getExistingUserUrl = `/api/v1/users/search?mobile=${values.mobile}`;
                setPatientSearchParameter({
                  type: "mobile",
                  value: values.mobile,
                });
              } else {
                getExistingUserUrl = `/api/v1/users/search?name=${values.searchName}`;
                setPatientSearchParameter({
                  type: "name",
                  value: values.searchName,
                });
              }
              HttpClient.performGet<any>(getExistingUserUrl)
                .then(({ body }) => {
                  if (
                    body?.profiles &&
                    body?.profiles.length === 1 &&
                    body?.profiles[0]
                  ) {
                    setMultipleProfiles([]);
                    if (isAppointmentTab) {
                      //validation to disallow booking from practo id.
                      if (!body?.profiles[0].appId) {
                        setSnackbar({
                          message:
                            "This patient is not registered on the IC4 app. Please register the patient on the IC4 app. IC4 registration number required for booking the appointment.",
                          severity: "error",
                        });
                        return;
                      }

                      setMainUserProfile({
                        name: body?.profiles[0].name,
                        clinic_patient_id:
                          body?.profiles[0].appId ??
                          body?.profiles[0].clinicPatientId,
                        mobile: body?.profiles[0].mobile,
                        email: body?.profiles[0].email,
                        appId: body?.profiles[0].appId,
                        doctorId: body?.profiles[0].doctorId,
                      });

                      //set prefered doctor to patients registered doctor
                      const foundDoctor =
                        receptionistProfile?.associatedHealthCareProviders?.find(
                          (doctor) => doctor.id === body?.profiles[0].doctorId,
                        );
                      if (foundDoctor) {
                        setPreferedDoctor(foundDoctor);
                      }

                      nextStep();
                    } else {
                      if (body?.profiles[0].appId) {
                        setMainUserProfile({
                          name: body?.profiles[0].name,
                          clinic_patient_id:
                            body?.profiles[0].appId ??
                            body?.profiles[0].clinicPatientId,
                          mobile: body?.profiles[0].mobile,
                          email: body?.profiles[0].email,
                          appId: body?.profiles[0].appId,
                          doctorId: body?.profiles[0].doctorId,
                        });
                        nextStep();
                      } else {
                        setSnackbar({
                          message: "Error fetching patient",
                          severity: "error",
                        });
                      }
                    }
                  } else if (body?.profiles && body?.profiles.length > 1) {
                    setMultipleProfiles(
                      body?.profiles.map(ProfileMapper.mapResponseToModel),
                    );
                  } else {
                    setMultipleProfiles([]);
                    setSnackbar({
                      message:
                        body?.status?.message ?? "Patient does not exist",
                      severity: "error",
                    });
                  }
                })
                .catch((e) => {
                  setMultipleProfiles([]);
                  setSnackbar({
                    message: e.body?.status?.message,
                    severity: "error",
                  });
                  console.log("finally!");
                  setSubmitting(false);
                });
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            submitForm,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="content clearfix">
                <section>
                  <div className="patient_reg_block">
                    <div className="row">
                      <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 gridcol">
                        <div className="left_patient_reg v_right_divider">
                          <div className="w-100 d-flex align-items-center text-decoration-underline justify-content-end mb-3">
                            <span
                              className="ms-auto btn btn-link "
                              style={{ color: "var(--primarycolor)" }}
                            ></span>
                          </div>
                          <div className="box_patient_reg">
                            <div className="row d-flex align-items-center">
                              <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                <div className="field_group">
                                  <label className="field_lbl">
                                    Patient Type
                                  </label>
                                </div>
                              </div>
                              <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                <div className="field_group">
                                  <ul className="list-unstyled list_gender_radio">
                                    <li>
                                      <label className="field_lbl">
                                        <input
                                          type="radio"
                                          name="patientype"
                                          value={"existing"}
                                          onChange={(e) =>
                                            setUserType("existing")
                                          }
                                          checked={userType === "existing"}
                                        />
                                        <span className="box_dot"></span>
                                        <span className="txt">Existing</span>
                                      </label>
                                    </li>
                                    {isAppointmentTab ? (
                                      <li>
                                        <label className="field_lbl">
                                          <input
                                            type="radio"
                                            name="patientype"
                                            value={"new"}
                                            checked={userType === "new"}
                                            onChange={(e) => setUserType("new")}
                                          />
                                          <span className="box_dot"></span>
                                          <span className="txt">New</span>
                                        </label>
                                      </li>
                                    ) : (
                                      <></>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                          {userType === "new" ? (
                            <>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">
                                        First name
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="text"
                                        className="field_input"
                                        name="firstName"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.firstName}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">
                                        Middle name
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="text"
                                        className="field_input"
                                        name="middleName"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.middleName}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">
                                        Last name
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="text"
                                        className="field_input"
                                        name="lastName"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.lastName}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">Email</label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="email"
                                        className="field_input"
                                        name="newEmail"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.newEmail}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">
                                        Mobile No
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="number"
                                        className="field_input"
                                        name="newMobile"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.newMobile}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">
                                        Patient ID
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="text"
                                        className="field_input"
                                        name="practoId"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.practoId}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">
                                        Mobile No
                                      </label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="text"
                                        className="field_input"
                                        name="mobile"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.mobile}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="box_patient_reg">
                                <div className="row d-flex align-items-center">
                                  <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                                    <div className="field_group">
                                      <label className="field_lbl">Name</label>
                                    </div>
                                  </div>
                                  <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                                    <div className="field_group">
                                      <input
                                        type="text"
                                        className="field_input"
                                        name="searchName"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.searchName}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {userType === "new" ? (
                        <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 gridcol">
                          <div className="right_patient_reg">
                            <div className="box_patient_reg">
                              <h4 className="maintitle_block mb-0">
                                Your preferred doctor
                              </h4>
                              <div className="mb-2">
                                <PreferredDoctors
                                  enabledDoctors={
                                    mainUserProfile?.doctorId
                                      ? [mainUserProfile!!.doctorId]
                                      : [userProfile.doctorId]
                                  }
                                  isCreateNewPatient={true}
                                  preferedDoctor={preferedDoctor}
                                  setPreferedDoctor={setPreferedDoctor}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 gridcol">
                          <div className="right_patient_reg">
                            <div className="box_patient_reg">
                              {multipleProfiles.length > 1 && (
                                <>
                                  <div className="primary-text">
                                    {patientSearchParameter?.type == "mobile"
                                      ? `
                                    Multiple Patient Registered with 
                                    ${multipleProfiles[0].mobile}`
                                      : patientSearchParameter?.type == "name"
                                        ? `
                                    Multiple Patient Registered with name
                                    ${patientSearchParameter?.value}`
                                        : ``}
                                  </div>
                                  <TableContainer component={"div"}>
                                    <Table aria-label="simple table">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Patient Name</TableCell>
                                          <TableCell>Patient ID</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {multipleProfiles.map((profile) => (
                                          <TableRow
                                            onClick={() => {
                                              //validation to disallow booking from practo id.
                                              if (!profile.appId) {
                                                setSnackbar({
                                                  message:
                                                    "This patient is not registered on the IC4 app. Please register the patient on the IC4 app. IC4 registration number required for booking the appointment.",
                                                  severity: "error",
                                                });
                                                return;
                                              }
                                              setMainUserProfile({
                                                ...profile,
                                                clinic_patient_id:
                                                  profile.appId ??
                                                  profile?.clinic_patient_id,
                                              });
                                              nextStep();
                                            }}
                                            style={{ cursor: "pointer" }}
                                            hover
                                            key={profile.name}
                                          >
                                            <TableCell
                                              component="th"
                                              scope="row"
                                            >
                                              {profile.name}
                                            </TableCell>
                                            <TableCell
                                              component="th"
                                              scope="row"
                                            >
                                              {profile.appId ??
                                                profile?.clinic_patient_id}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </div>
              {isAppointmentTab ? (
                <div className="actions clearfix">
                  <ul role="menu" aria-label="Pagination">
                    <li style={{ visibility: "hidden" }}>
                      <span />
                    </li>
                    <li aria-hidden="false" aria-disabled="false">
                      <button type={"submit"}>Next</button>
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="actions clearfix">
                  <ul role="menu" aria-label="Pagination">
                    <li style={{ visibility: "hidden" }}>
                      <span />
                    </li>
                    <li aria-hidden="false" aria-disabled="false">
                      <button>Upload Reports</button>
                    </li>
                  </ul>
                </div>
              )}
            </form>
          )}
        </Formik>
      </div>
    );
  };

  const Step2 = () => {
    const upcomingUserVisit = useUpcomingUserVisit(
      mainUserProfile?.clinic_patient_id || "",
      userProfile,
    );

    const [isCancelDialogVisible, setCancelDialogVisibility] =
      React.useState(false);
    const [selectedAppointment, setSelectedAppointment] =
      React.useState<AppointmentInfo>({
        id: "",
        patientName: "",
      });
    const { nextStep, previousStep } = useWizard();
    const [isNextDisabled, disableNext] = React.useState(false);

    //This is to make only 1 appointment visible by default when there are multiple upcoming appointments and show a toggle button to show the rest of the appointments. This is to avoid overwhelming the receptionist with too much information at once and to make the UI cleaner. Receptionist can click on the toggle button to view the rest of the appointments if they want to.
    const [showAllAppointments, setShowAllAppointments] = useState(false);
    const appointments =
      upcomingUserVisit.upcomingAppointments?.resource?.appointments ?? [];
    const firstAppointment = appointments[0];
    const remainingAppointments = appointments.slice(1);

    const openCancelDialog = (
      event: React.MouseEvent<HTMLElement>,
      appointment: AppointmentInfo,
    ) => {
      event.preventDefault();
      setSelectedAppointment(appointment);
      setCancelDialogVisibility(true);
    };

    const closeCancelDialog = (toRefresh: boolean = false) => {
      if (toRefresh) {
        upcomingUserVisit.upcomingAppointments.refresh();
        upcomingUserVisit.upcomingWaitlist.refresh();
      }
      setCancelDialogVisibility(false);
    };

    const isUpcomingVisitLoading = () =>
      upcomingUserVisit.upcomingAppointments.isLoading ||
      upcomingUserVisit.upcomingWaitlist.isLoading;
    const upcomingVisitHasError = () =>
      upcomingUserVisit.upcomingAppointments.error !== undefined &&
      upcomingUserVisit.upcomingWaitlist.error !== undefined;
    const upcomingVisitError = () => {
      if (upcomingUserVisit.upcomingAppointments.error)
        return upcomingUserVisit.upcomingAppointments.error;
      else if (upcomingUserVisit.upcomingWaitlist.error)
        return upcomingUserVisit.upcomingWaitlist.error;
      else return "";
    };
    const hasUpcomingAppointment = () =>
      upcomingUserVisit.upcomingAppointments.resource?.status.success &&
      upcomingUserVisit.upcomingAppointments.resource.appointments.length > 0;
    const hasUpcomingWaitlist = () =>
      upcomingUserVisit.upcomingWaitlist.resource?.status.success &&
      upcomingUserVisit.upcomingWaitlist.resource.waitlist;

    const UpcomingAppointmentSummary = (props: {
      upcomingAppointment: UpcomingUserAppointments;
    }) => (
      <>
        <div className="primary-text">
          The appointment is already scheduled.
        </div>
        <AppointmentCard
          mainUserProfile={mainUserProfile!}
          pid={searchParams.get("pid") ?? ""}
          doctorName={
            props.upcomingAppointment.appointment!.owner_metadata.name
          }
          appointmentDate={props.upcomingAppointment.appointment!.date}
          time={
            props.upcomingAppointment.appointment?.end_time
              ? `${props.upcomingAppointment.appointment.start_time} - ${props.upcomingAppointment.appointment.end_time}`
              : props.upcomingAppointment.appointment?.start_time
          }
          clinic={props.upcomingAppointment.appointment!.space_metadata}
          attendeeStatus={
            props.upcomingAppointment.appointment?.attendee_status
          }
          isOnline={props.upcomingAppointment.appointment?.isOnline}
          therapy={props.upcomingAppointment.appointment?.therapy?.name}
        />
        {props.upcomingAppointment.appointment?.attendee_status !==
          "Arrived" && (
          <div className="actions-clone clearfix">
            <ul role="menu" aria-label="Pagination">
              <li aria-hidden="false" aria-disabled="false">
                <a
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    openCancelDialog(e, {
                      id: props.upcomingAppointment.appointment!.id,
                      patientName:
                        props.upcomingAppointment.appointment!.attendee_metadata
                          .name,
                    });
                  }}
                  className="link_cancel"
                  style={{ cursor: "pointer" }}
                >
                  Cancel
                </a>
              </li>
              <li aria-hidden="false" aria-disabled="false">
                {isScheduledBySelf(
                  props.upcomingAppointment.appointment!.space_metadata.name,
                ) && (
                  <Link
                    reloadDocument={true}
                    to={`/app-help?w=2&id=${
                      props.upcomingAppointment.appointment!.id
                    }&pid=${utils.getPatientId(
                      props.upcomingAppointment.appointment!,
                    )}&did=${
                      props.upcomingAppointment.appointment!.owner_metadata.code
                    }&reschedule=true`}
                    className="link_cancel me-3"
                  >
                    Reschedule Appointment
                  </Link>
                )}
              </li>
            </ul>
          </div>
        )}
        <CancelAppointmentDialog
          appointmentInfo={selectedAppointment}
          isOpen={isCancelDialogVisible}
          closeFn={() => closeCancelDialog(true)}
        />
      </>
    );

    const UpcomingWaitlistSummary = (props: {
      upcomingWaitlist: UpcomingUserWaitlist;
    }) => (
      <>
        <div className="primary-text">The waitlist is already scheduled.</div>
        <AppointmentSummary
          mainUserProfile={mainUserProfile!}
          pid={searchParams.get("pid") ?? ""}
          doctorName={props.upcomingWaitlist.waitlist!.owner_metadata.name}
          appointmentDate={props.upcomingWaitlist.waitlist!.date}
          time={props.upcomingWaitlist.waitlist!.startTime}
          clinic={props.upcomingWaitlist.waitlist?.space_metadata!}
          attendeeStatus={undefined}
          isOnline={false}
          therapy={undefined}
        />
      </>
    );

    const UpcomingVisitSummary = () => (
      <>
        <br />
        <h4 className="maintitle_block">
          {hasUpcomingWaitlist() ? (
            <>Waitlist Booking Details</>
          ) : (
            <>Appointment Booking Details</>
          )}
        </h4>
        {isUpcomingVisitLoading() ? (
          <>checking for upcoming appointments and waitlist...</>
        ) : upcomingVisitHasError() ? (
          <>{utils.getErrorMessage(JSON.stringify(upcomingVisitError()))}</>
        ) : hasUpcomingAppointment() ? (
          <div className="container">
            {/* Always show first appointment */}
            {firstAppointment && (
              <UpcomingAppointmentSummary
                key={firstAppointment.appointment?.id}
                upcomingAppointment={firstAppointment}
              />
            )}

            {/* Show toggle button only if more exist */}
            {remainingAppointments.length > 0 && (
              <div className="text-center my-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowAllAppointments(!showAllAppointments)}
                >
                  {showAllAppointments
                    ? "Hide other appointments"
                    : `Show ${remainingAppointments.length} more appointment(s)`}
                </button>
              </div>
            )}

            {/* Collapsible Section */}
            {showAllAppointments &&
              remainingAppointments.map((appointment) => (
                <UpcomingAppointmentSummary
                  key={appointment.appointment?.id}
                  upcomingAppointment={appointment}
                />
              ))}
          </div>
        ) : hasUpcomingWaitlist() ? (
          <UpcomingWaitlistSummary
            upcomingWaitlist={upcomingUserVisit.upcomingWaitlist!.resource!}
          />
        ) : (
          <>{/*upcomingUserAppointment.resource?.status.message*/}</>
        )}
      </>
    );

    return (
      <div className="second_step">
        <div className="content clearfix">
          <section>
            <div className="patient_reg_block">
              <div className="row">
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 gridcol">
                  <div className="left_patient_reg v_right_divider">
                    <div className="w-100 d-flex align-items-center text-decoration-underline justify-content-end mb-3">
                      <span
                        className="ms-auto btn btn-link "
                        onClick={() => {
                          resetBookingState();
                          previousStep();
                        }}
                        style={{ color: "var(--primarycolor)" }}
                      >
                        Clear / New Patient
                      </span>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                          <div className="field_group">
                            <label className="field_lbl">Patient Type</label>
                          </div>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                          <div className="field_group">
                            <ul className="list-unstyled list_gender_radio">
                              <li>
                                <label className="field_lbl">
                                  <input
                                    type="radio"
                                    name="patientype"
                                    checked={userType === "existing"}
                                    onChange={(e) => null}
                                  />
                                  <span className="box_dot"></span>
                                  <span className="txt">Existing</span>
                                </label>
                              </li>
                              {isAppointmentTab ? (
                                <li>
                                  <label className="field_lbl">
                                    <input
                                      type="radio"
                                      name="patientype"
                                      checked={userType === "new"}
                                      onChange={(e) => null}
                                    />
                                    <span className="box_dot"></span>
                                    <span className="txt">New</span>
                                  </label>
                                </li>
                              ) : (
                                <></>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                          <div className="field_group">
                            <label className="field_lbl">Patient ID</label>
                          </div>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                          <div className="field_group">
                            <input
                              type="text"
                              className="field_input"
                              onChange={(e) => null}
                              value={mainUserProfile?.clinic_patient_id}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                          <div className="field_group">
                            <label className="field_lbl">Mobile No</label>
                          </div>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                          <div className="field_group">
                            <input
                              type="text"
                              className="field_input"
                              onChange={(e) => null}
                              value={mainUserProfile?.mobile}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 fieldcol">
                          <div className="field_group">
                            <label className="field_lbl">Name</label>
                          </div>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 fieldcol">
                          <div className="field_group">
                            <input
                              type="text"
                              className="field_input"
                              onChange={(e) => null}
                              value={mainUserProfile?.name}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 gridcol">
                  <div className="right_patient_reg">
                    <div className="box_patient_reg">
                      <h4 className="maintitle_block mb-0">
                        Your preferred doctor
                      </h4>
                      <div className="mb-2">
                        <PreferredDoctors
                          enabledDoctors={(() => {
                            /**
                             * Rules for enabled doctors:
                             *
                             * 1. Dietitian → only their own doctor ID
                             * 2. If main user's doctor exists in this clinic → only that doctor(made change later and allowed all clinic doctors)
                             * 3. If main user's doctor exists BUT not in this clinic → all  doctors
                             * 4. Fallback → current user's doctor
                             */

                            // Rule 1
                            if (
                              userProfile.healthCareProviderType === "Dietitian"
                            ) {
                              return [userProfile.doctorId];
                            }

                            // Rule 2 & 3
                            if (mainUserProfile?.doctorId) {
                              // const isAssociated =
                              //   userProfile.associatedHealthCareProviders?.some(
                              //     (p) => p.id === mainUserProfile?.doctorId
                              //   );

                              // return isAssociated
                              //   ? [mainUserProfile.doctorId]
                              //   : userProfile.associatedHealthCareProviders?.map(
                              //       (p) => p.id
                              //     ) ?? [userProfile?.doctorId];
                              return (
                                userProfile.associatedHealthCareProviders?.map(
                                  (p) => p.id,
                                ) ?? [userProfile?.doctorId]
                              );
                            }

                            // Rule 4
                            return [userProfile.doctorId];
                          })()}
                          isCreateNewPatient={false}
                          preferedDoctor={
                            //If upcoming appointment's doctor is in this clinic, set that as preferred doctor
                            //else set patients doctor as preferred doctor

                            (userProfile.associatedHealthCareProviders?.find(
                              (doctor) =>
                                doctor.id ===
                                upcomingUserVisit.upcomingAppointments?.resource
                                  ?.appointments[0].appointment?.owner_metadata
                                  .code,
                            ) ??
                              userProfile.associatedHealthCareProviders?.find(
                                (doctor) =>
                                  doctor.id === mainUserProfile?.doctorId,
                              )) ||
                            null
                          }
                          setPreferedDoctor={null}
                        />
                      </div>
                      <UpcomingVisitSummary />
                    </div>
                  </div>
                </div>
              </div>
              {showUploadDialog &&
                mainUserProfile?.clinic_patient_id !== null && (
                  <UploadReportDialog
                    closeFn={() => setShowUploadDialog(false)}
                    patientId={mainUserProfile?.clinic_patient_id!!}
                    isFromAppointment={false}
                  />
                )}
            </div>
          </section>
        </div>

        <div className="actions clearfix">
          <ul role="menu" aria-label="Pagination">
            <li aria-hidden="false" aria-disabled="false">
              <span
                onClick={() => {
                  resetBookingState();
                  previousStep();
                }}
              >
                Back
              </span>
            </li>
            {isAppointmentTab ? (
              <li aria-hidden="false" aria-disabled="false">
                <a
                  className={isNextDisabled ? "btn disabled" : "btn"}
                  role="button"
                  aria-disabled="true"
                >
                  <span
                    onClick={() => {
                      nextStep();
                    }}
                  >
                    Next
                  </span>
                </a>
              </li>
            ) : (
              <li aria-hidden="false" aria-disabled="false">
                <a className={false ? "btn disabled" : "btn"} role="button">
                  <span onClick={() => setShowUploadDialog(true)}>
                    Upload Reports
                  </span>
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  const Step3 = () => {
    const { nextStep, previousStep, goToStep } = useWizard();
    const navigate = useNavigate();

    const selectedHealthProvider =
      utils.getDoctorProfileForAppointmentBooking();

    const isCustomTimeMode = selectedHealthProvider.scheduleType === "ADHOC";

    const today = new Date().toISOString().split("T")[0];

    // ------------------------------
    // userProfile & oldAppointmentId (from original Step3)
    // ------------------------------
    const userProfile = useExistingUser(
      searchParams.get("pid")
        ? {
            patientId: searchParams.get("pid") ?? "",
          }
        : undefined,
    );

    useEffect(() => {
      setOldAppointmentId(searchParams.get("id") ?? "");
    }, []);

    // ------------------------------
    // Calendar state (from original Step3)
    // ------------------------------
    const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
    const [calendarEventsRaw, setCalendarEventsRaw] = useState<any[]>([]);

    const calendarSlots = useAppointmentSlots({
      days: "60",
      spaceId: receptionistProfile.clinicId,
      ownerId: selectedHealthProvider.id,
      fromDate: today,
      reschedule: searchParams.get("reschedule") ? "true" : "false",
      attendeeId:
        mainUserProfile?.clinic_patient_id ?? searchParams.get("pid") ?? "",
    });

    useEffect(() => {
      const dates = calendarSlots?.resource?.dates;
      if (dates) {
        const availableDates = dates.filter((date) => {
          return (
            date.available ||
            date.buffer_available ||
            (date.slots && date.slots.length > 0)
          );
        });
        const eventDates =
          availableDates
            ?.map((date) => {
              return date.date;
            })
            .map((dateString) => {
              return {
                groupId: "available",
                start: dateString,
                end: dateString,
                display: "inverse-background",
              };
            }) || [];
        setCalendarEvents(eventDates);
        setCalendarEventsRaw(dates);
      }
    }, [calendarSlots.resource]);

    // ------------------------------
    // Date click handler (from original Step3 - NO nextStep!)
    // ------------------------------
    const handleDateClick = (arg: any) => {
      const isValidDate = calendarEvents.some((event: any) => {
        return event.start === arg?.dateStr;
      });

      if (isValidDate) {
        if (searchParams.get("w")) {
          setMainUserProfile({
            name: userProfile?.resource?.profiles[0].name || "",
            clinic_patient_id:
              userProfile?.resource?.profiles[0].appId ||
              userProfile?.resource?.profiles[0]?.clinic_patient_id ||
              "",
            mobile: userProfile?.resource?.profiles[0].mobile || "",
            email: userProfile?.resource?.profiles[0].email || "",
            appId: userProfile?.resource?.profiles[0].appId || "",
            doctorId: userProfile?.resource?.profiles[0].doctorId || "",
          });
        }
        setSelectedDate(arg?.dateStr);
      }
    };

    // ------------------------------
    // Helper functions (from original Step3)
    // ------------------------------
    function padTo2Digits(num: number): string {
      return num.toString().padStart(2, "0");
    }

    function formatDate(date: Date): string {
      return [
        date.getFullYear(),
        padTo2Digits(date.getMonth() + 1),
        padTo2Digits(date.getDate()),
      ].join("-");
    }

    function getRawEvent(date: string): AppointmentDate {
      return calendarEventsRaw.find((e) => e.date == date);
    }

    const dayCell = (arg: any) => {
      const _date = formatDate(arg.date);
      const _eventDetails = getRawEvent(_date);
      let _cssClass = "cd-slots-nostyle";
      if (_eventDetails && _eventDetails.slots.length > 0) {
        if (_eventDetails.available) {
          _cssClass = "cd-slots-available";
        } else if (!_eventDetails.available && _eventDetails.buffer_available) {
          _cssClass = "cd-slots-almost-full";
        } else {
          _cssClass = "cd-slots-full";
        }
      }
      const _html = `<div class="cd-slot-container"><div class="cd-slots-default ${_cssClass}">${arg.dayNumberText}</div></div>`;
      return { html: _html };
    };

    // ------------------------------
    // Slots for selected date (from original Step4)
    // ------------------------------
    const daySlots = useAppointmentSlots({
      days: "1",
      spaceId: receptionistProfile.clinicId,
      ownerId: selectedHealthProvider.id,
      fromDate: selectedDate,
      reschedule: searchParams.get("reschedule") ? "true" : "false",
      attendeeId:
        mainUserProfile?.clinic_patient_id ?? searchParams.get("pid") ?? "",
    });

    // ------------------------------
    // Therapies (from original Step4)
    // ------------------------------
    const {
      resource: therapies,
      isLoading: therapiesLoading,
      error: therapiesError,
      refresh,
    } = useClinicTherapies(selectedHealthProvider.id);

    const bookedAppointments = useAppointmentList({
      date: selectedDate,
      ownerId: selectedHealthProvider.id,
    });

    const bookedAppointmentsList =
      bookedAppointments?.resource?.appointments ?? [];

    // ------------------------------
    // Custom time state (from original Step4)
    // ------------------------------
    const [startTime, setStartTime] = useState<any>();
    const [endTime, setEndTime] = useState<any>();
    const [endTimeId, setEndTimeId] = useState<number | null>(null);
    const [notes, setNotes] = useState<string>("");

    const durationInMinutes = useMemo(() => {
      if (!startTime || !endTime) return null;

      const start = moment(startTime.time, "hh:mm A");
      const end = moment(endTime.time, "hh:mm A");

      if (!start.isValid() || !end.isValid()) return null;

      const diff = end.diff(start, "minutes");
      return diff > 0 ? diff : null;
    }, [startTime, endTime]);

    useEffect(() => {
      if (!startTime || !selectedTherapy) {
        if (endTimeId !== null || endTime !== null) {
          setEndTimeId(null);
          setEndTime(null);
        }
        return;
      }

      const therapyDuration = getTherapyDurationInMinutes(selectedTherapy);
      if (!therapyDuration) return;

      const endSlots = startTime.end_time_slots || [];
      if (!endSlots.length) return;

      const matchedSlot = endSlots.find((slot: any) => {
        const match = slot.time.match(/\((\d+)\s*min\)/);
        const minutes = match ? Number(match[1]) : null;

        return minutes !== null && minutes >= therapyDuration && slot.available;
      });

      const newId = matchedSlot?.id ?? null;

      if (newId !== endTimeId) {
        setEndTimeId(newId);
        setEndTime(matchedSlot ?? null);
      }
    }, [startTime, selectedTherapy]);

    // ------------------------------
    // Helpers (from original Step4)
    // ------------------------------
    const getSlotClass = (slot: AppointmentDateSlot) => {
      if (!slot.available) return "disabled";

      if (selectedTimeSlot?.id === slot.id) {
        return "selected"; // 👈 custom class
      }

      return slot.slot_type === "Normal" ? "active" : "current";
    };

    const getTherapyDurationInMinutes = (therapy?: Therapy | null) => {
      if (!therapy) return null;

      switch (therapy.durationUnit) {
        case "minutes":
          return therapy.duration;
        case "hours":
          return therapy.duration * 60;
        default:
          return therapy.duration;
      }
    };

    //for displaying calendar selected date
    const calendarDate = useMemo(() => {
      if (!selectedDate) return new Date(); // 👈 critical
      return new Date(selectedDate + "T00:00:00"); // avoid timezone issues
    }, [selectedDate]);

    // ------------------------------
    // Loading / Error (from original Step3)
    // ------------------------------
    if (calendarSlots.isLoading) {
      return <h1>Loading...</h1>;
    }
    if (calendarSlots.error) {
      return (
        <Alert severity="error">{JSON.stringify(calendarSlots.error)}</Alert>
      );
    }

    return (
      <>
        <div className="third_step">
          <div className="content clearfix">
            <section>
              <div className="row">
                {/* ================= LEFT: CALENDAR ================= */}
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12">
                  <div className="calendar_block" id="kt_calendar">
                    <FullCalendar
                      plugins={[dayGridPlugin, interactionPlugin]}
                      selectable={true}
                      initialDate={calendarDate}
                      initialView="dayGridMonth"
                      dayCellContent={dayCell}
                      dayCellClassNames={(arg) => {
                        if (!selectedDate) return "";

                        // Format both dates consistently using the same method
                        const cellDate = formatDate(arg.date);
                        return cellDate === selectedDate
                          ? "fc-day-selected"
                          : "";
                      }}
                      showNonCurrentDates={true}
                      themeSystem={"bootstrap5"}
                      eventShortHeight={30}
                      aspectRatio={1.5}
                      events={calendarEvents}
                      eventBackgroundColor={"#dddddd"}
                      eventOverlap={false}
                      nowIndicator={false}
                      selectMirror={true}
                      unselectAuto={false}
                      dateClick={handleDateClick}
                      headerToolbar={{
                        start: "prev",
                        center: "title",
                        end: "next",
                      }}
                    />
                  </div>
                </div>

                {/* ================= RIGHT: TIME SLOTS ================= */}
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12">
                  {!selectedDate && (
                    <div className="text-muted text-center mt-5">
                      <p>
                        Please select a date from the calendar to view available
                        time slots
                      </p>
                    </div>
                  )}

                  {selectedDate && (
                    <>
                      {/* -------- SLOT-BASED UI (from original Step4) -------- */}
                      {!isCustomTimeMode && (
                        <>
                          {daySlots.isLoading && <h3>Loading...</h3>}
                          {daySlots.error && (
                            <Alert severity="error">
                              {JSON.stringify(daySlots.error)}
                            </Alert>
                          )}
                          {!daySlots.isLoading && !daySlots.error && (
                            <ul className="list-unstyled list_timeslot">
                              {daySlots.resource?.dates?.[0]?.slots.map(
                                (slot, index) => (
                                  <li key={index}>
                                    <a
                                      href="javascript:void(0)"
                                      className={getSlotClass(slot)}
                                      onClick={() => {
                                        if (!slot.available) return;
                                        setSelectedTimeSlot(slot);
                                      }}
                                    >
                                      <span className="txt">{slot.time}</span>
                                    </a>
                                  </li>
                                ),
                              )}
                            </ul>
                          )}
                        </>
                      )}

                      {/* -------- CUSTOM TIME MODE (from original Step4) -------- */}
                      {isCustomTimeMode && (
                        <>
                          {daySlots.isLoading && <h3>Loading...</h3>}
                          {!daySlots.isLoading && (
                            <Box
                              style={{
                                background: "#fff",
                                borderRadius: 12,
                                padding: 16,
                              }}
                            >
                              {/* Therapy / Session Type */}
                              <div className="my-2">
                                Date:{" "}
                                <span className="fw-bold">
                                  {moment(new Date(selectedDate)).format(
                                    "Do MMM, dddd, YYYY",
                                  )}
                                </span>
                              </div>
                              {bookedAppointmentsList.length > 0 ? (
                                <div className="my-2">
                                  <ul className="mb-0 ps-3 bg-white text-muted">
                                    {bookedAppointmentsList.map(
                                      (appointment) => (
                                        <li
                                          key={
                                            appointment?.id ??
                                            `${appointment?.start_time}-${appointment?.end_time}`
                                          }
                                          className="list-disc"
                                        >
                                          <span className="small me-1">
                                            {appointment?.start_time} -{" "}
                                            {appointment?.end_time} -{" "}
                                            {appointment?.therapy?.name}
                                          </span>
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              ) : (
                                <div className="my-2">
                                  <ul className="mb-0 ps-3 bg-white text-muted">
                                    <li className="small text-muted fst-italic">
                                      No appointments scheduled
                                    </li>
                                  </ul>
                                </div>
                              )}
                              <FormControl fullWidth>
                                <Autocomplete
                                  options={therapies || []}
                                  getOptionLabel={(option) => option?.name}
                                  value={selectedTherapy}
                                  onChange={(_, newValue) => {
                                    setSelectedTherapy(newValue);
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Therapy / Session Type"
                                      size="small"
                                    />
                                  )}
                                />
                              </FormControl>

                              {/* Start / End time row */}
                              <Box
                                display="flex"
                                alignItems="flex-end"
                                gridGap={8}
                                className="mt-3"
                              >
                                <Box flex={1}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Start Time</InputLabel>
                                    <Select
                                      label="Start Time"
                                      value={startTime}
                                      onChange={(e) =>
                                        setStartTime(e.target.value)
                                      }
                                      renderValue={(selected) =>
                                        (selected as any)?.time || ""
                                      }
                                    >
                                      {daySlots.resource?.dates[0].slots?.map(
                                        (opt: any) => (
                                          <MenuItem
                                            key={opt.time}
                                            value={opt}
                                            disabled={!opt.available}
                                          >
                                            {opt.time}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>

                                <Box flex={1}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>End Time</InputLabel>
                                    <Select
                                      label="End Time"
                                      value={endTimeId ?? ""}
                                      disabled={!startTime}
                                      onChange={(e) => {
                                        const selectedId = e.target
                                          .value as number;

                                        const selectedSlot =
                                          startTime?.end_time_slots?.find(
                                            (s: any) => s.id === selectedId,
                                          );

                                        setEndTimeId(selectedId);
                                        setEndTime(selectedSlot ?? null);
                                      }}
                                      renderValue={(selectedId) => {
                                        const slot =
                                          startTime?.end_time_slots?.find(
                                            (s: any) => s.id === selectedId,
                                          );
                                        return slot?.time ?? "";
                                      }}
                                    >
                                      {startTime?.end_time_slots?.map(
                                        (opt: any) => (
                                          <MenuItem
                                            key={opt.id}
                                            value={opt.id}
                                            disabled={!opt.available}
                                          >
                                            {opt.time}
                                          </MenuItem>
                                        ),
                                      )}
                                    </Select>
                                  </FormControl>
                                </Box>

                                {/* Duration pill */}
                                <Box mb={0.5}>
                                  {durationInMinutes !== null && (
                                    <Chip
                                      label={`${durationInMinutes} min`}
                                      style={{
                                        background: "#E3F2FD",
                                        color: "#1976D2",
                                        fontWeight: 500,
                                        height: 36,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              {/* Notes */}
                              <Box mt={2}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  variant="outlined"
                                  size="small"
                                  placeholder="Session notes (optional)..."
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                />
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </section>
          </div>

          {/* ================= FOOTER (from original Step3) ================= */}
          <div className="actions clearfix">
            <ul role="menu" aria-label="Pagination">
              <li aria-hidden="false" aria-disabled="false">
                <span
                  onClick={() => {
                    resetBookingState();
                    if (oldAppointmentId) {
                      navigate(-1);
                    } else {
                      previousStep();
                    }
                  }}
                >
                  Back
                </span>
              </li>
              <li className="d-flex gap-2">
                {!isCustomTimeMode && selectedDate && (
                  <li aria-hidden="false" aria-disabled="false">
                    {daySlots?.resource?.dates?.[0]?.available ||
                    daySlots?.resource?.dates?.[0]?.buffer_available ? (
                      <span className="btn  disabled" role="button">
                        Add to Waiting List
                      </span>
                    ) : (
                      <span
                        onClick={() => {
                          goToStep(4);
                        }}
                        className="btn"
                        role="button"
                      >
                        Add to Waiting List
                      </span>
                    )}
                  </li>
                )}

                {isCustomTimeMode && selectedDate && (
                  <li aria-hidden="false" aria-disabled="false">
                    <span
                      className={`btn ${!startTime || !endTime || !selectedTherapy ? "disabled" : ""}`}
                      style={{
                        opacity:
                          !startTime || !endTime || !selectedTherapy ? 0.5 : 1,
                        cursor:
                          !startTime || !endTime || !selectedTherapy
                            ? "not-allowed"
                            : "pointer",
                      }}
                      onClick={() => {
                        if (!startTime || !endTime || !selectedTherapy) {
                          return;
                        }
                        setSelectedTimeSlot(startTime);
                        setSelectedEndTimeSlot(endTime);
                        setSelectedTherapy(selectedTherapy);
                        setTherapyBookingNote(notes);
                        nextStep();
                      }}
                    >
                      Continue
                    </span>
                  </li>
                )}
                {!isCustomTimeMode && selectedDate && (
                  <li aria-hidden="false" aria-disabled="false">
                    <span
                      className={`btn ${!selectedTimeSlot ? "disabled" : ""}`}
                      style={{
                        opacity: !selectedTimeSlot ? 0.5 : 1,
                        cursor: !selectedTimeSlot ? "not-allowed" : "pointer",
                      }}
                      onClick={() => {
                        if (!selectedTimeSlot) return;
                        nextStep();
                      }}
                    >
                      Continue
                    </span>
                  </li>
                )}
              </li>
            </ul>
          </div>
        </div>
      </>
    );
  };

  const Step4 = () => {
    const spaceId = receptionistProfile.clinicId;
    const navigate = useNavigate();
    const { previousStep, goToStep } = useWizard();
    // const { submit, isLoading, error } = useCreateAppointment("CC000001");
    const { setSnackbar } = useSnackbarContext();
    // ownerId=DR000001 oldAppointmentId

    return (
      <div className="fifth_step">
        <div className="content clearfix">
          <section>
            <div className="patient_reg_block">
              <div className="row">
                <div className="col-xxl-6 col-xl-6 col-lg-7 col-md-8 gridcol">
                  <AppointmentSummary
                    mainUserProfile={mainUserProfile!}
                    pid={searchParams.get("pid") ?? ""}
                    appointmentDate={selectedDate}
                    doctorName={
                      utils.getDoctorProfileForAppointmentBooking().name
                    }
                    clinic={new SpaceMetadata(userProfile.clinicName)}
                    time={selectedTimeSlot ? selectedTimeSlot.time : ""}
                    attendeeStatus={undefined}
                    isOnline={false}
                    therapy={selectedTherapy?.name}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="actions clearfix">
          <ul role="menu" aria-label="Pagination">
            <li aria-hidden="false" aria-disabled="false">
              <span onClick={() => previousStep()}>Back</span>
            </li>
            <li
              className={"d-flex flex-row"}
              aria-hidden="false"
              aria-disabled="false"
            >
              <span
                className={"me-2"}
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </span>
              <span
                onClick={() => {
                  const selectedHealthProvider =
                    utils.getDoctorProfileForAppointmentBooking();

                  const isCustomTimeMode =
                    selectedHealthProvider?.scheduleType === "ADHOC";

                  const ownerId = selectedHealthProvider?.id;

                  // 🔒 Guard for custom mode
                  if (
                    isCustomTimeMode &&
                    (selectedEndTimeSlot?.id == null ||
                      selectedTherapy?.id == null)
                  ) {
                    console.log(selectedEndTimeSlot, selectedTherapy);
                    setSnackbar({
                      message: "Please select therapy and end time",
                      severity: "error",
                    });
                    return;
                  }

                  // --------------------------------------------------
                  // 🔁 RESCHEDULE (PUT)
                  // --------------------------------------------------
                  if (oldAppointmentId) {
                    const params = new URLSearchParams({
                      slotId: String(selectedTimeSlot?.id),
                      ownerId: String(ownerId),
                    });

                    if (isCustomTimeMode) {
                      params.append("endTime", String(selectedEndTimeSlot!.id));
                      params.append("therapyId", String(selectedTherapy!.id));
                      params.append("note", therapyBookingNote ?? "");
                    }

                    HttpClient.performPut<any>(
                      `/api/v1/appointments/${oldAppointmentId}/reschedule?${params.toString()}`,
                      {
                        online:
                          userProfile.healthCareProviderType === "Dietitian",
                      },
                    )
                      .then((response) => {
                        if (!response?.body?.status?.success) {
                          setSnackbar({
                            message: response?.body?.status?.message,
                            severity: "error",
                          });
                        } else {
                          setSnackbar({
                            message: "Appointment rescheduled successfully!",
                            severity: "success",
                          });
                          navigate("/today-appointment", { replace: false });
                        }
                      })
                      .catch(console.error);

                    return;
                  }

                  // --------------------------------------------------
                  // 🆕 CREATE (POST)
                  // --------------------------------------------------

                  // Only add query params in CUSTOM mode
                  const postParams = isCustomTimeMode
                    ? new URLSearchParams({
                        endTime: String(selectedEndTimeSlot!.id),
                        therapyId: String(selectedTherapy!.id),
                      }).toString()
                    : "";

                  const requestBody: any = {
                    slotId: selectedTimeSlot?.id!,
                    attendeeId:
                      mainUserProfile?.clinic_patient_id ??
                      searchParams.get("pid") ??
                      "",
                    ownerId,
                    online: userProfile.healthCareProviderType === "Dietitian",
                  };

                  // Add note only in custom time mode
                  if (isCustomTimeMode && therapyBookingNote) {
                    requestBody.note = therapyBookingNote;
                  }
                  HttpClient.performPost<any>(
                    `/api/v1/appointments/${spaceId}${
                      postParams ? `?${postParams}` : ""
                    }`,
                    requestBody,
                  )
                    .then((response) => {
                      if (!response?.body?.status?.success) {
                        setSnackbar({
                          message: response?.body?.status?.message,
                          severity: "error",
                        });
                      } else {
                        setSnackbar({
                          message: "Appointment booked successfully!",
                          severity: "success",
                        });
                        // navigate("/today-appointment", { replace: false });
                        setSessionBookings((prev) => [
                          ...prev,
                          {
                            date: selectedDate,
                            startTime: selectedTimeSlot?.time || "",
                            endTime: selectedEndTimeSlot?.time,
                            patientName: mainUserProfile?.name || "",
                            doctorName:
                              utils.getDoctorProfileForAppointmentBooking()
                                ?.name || "",
                            clinicName: receptionistProfile.clinicName,
                            therapyName: selectedTherapy?.name,
                          },
                        ]);
                        goToStep(5);
                      }
                    })
                    .catch(console.error);
                }}
              >
                Submit
              </span>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const Step5 = () => {
    const spaceId = receptionistProfile.clinicId;
    const navigate = useNavigate();
    const { goToStep } = useWizard();
    // const { submit, isLoading, error } = useCreateAppointment("CC000001");
    const { setSnackbar } = useSnackbarContext();
    // ownerId=DR000001 oldAppointmentId
    const [note, setNote] = useState<string>("Missed Appointment");
    const [location, setLocation] = useState<string>("");
    const noteList = [
      "Missed Appointment",
      "Flair Up",
      "Ready to come at anytime",
    ];

    return (
      <div className="sixth_step">
        <div className="content clearfix">
          <section>
            <div className="patient_reg_block">
              <div className="row">
                <div className="col-xxl-6 col-xl-6 col-lg-7 col-md-8 gridcol">
                  <div className="left_patient_reg">
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="big_headtitle mb-2">
                          Add to Waiting List
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-5 fieldcol">
                          <span className="txt_field">Patient ID :</span>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 col-md-7 fieldcol">
                          <span className="txt_field fw-bolder">
                            {mainUserProfile?.clinic_patient_id ??
                              searchParams.get("pid") ??
                              ""}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-5 fieldcol">
                          <span className="txt_field field_title">
                            Patient Name :
                          </span>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 col-md-7 fieldcol">
                          <span className="txt_field fw-bolder">
                            {mainUserProfile?.name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-5 fieldcol">
                          <span className="txt_field">Doctor :</span>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 col-md-7 fieldcol">
                          <span className="txt_field fw-bolder">
                            {receptionistProfile.associatedHealthCareProviders.find(
                              (healthcareProvider) =>
                                healthcareProvider.id ===
                                mainUserProfile?.doctorId,
                            )?.name || receptionistProfile.doctorName}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-5 fieldcol">
                          <span className="txt_field">Clinic :</span>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 col-md-7 fieldcol">
                          <span className="txt_field fw-bolder">
                            {receptionistProfile.clinicName}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className="row d-flex align-items-center">
                        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-5 fieldcol">
                          <span className="txt_field">Date :</span>
                        </div>
                        <div className="col-xxl-9 col-xl-9 col-lg-9 col-md-7 fieldcol">
                          <span className="txt_field fw-bolder">
                            {moment(new Date(selectedDate)).format(
                              "Do MMM, dddd, YYYY",
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="box_patient_reg">
                      <div className={"row"}>
                        <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 ">
                          <FormControl fullWidth>
                            <InputLabel htmlFor={"location-outlined"}>
                              Location/Patient Coming from
                            </InputLabel>
                            <Input
                              id="location-outlined"
                              placeholder={"Location"}
                              autoComplete={"off"}
                              value={location}
                              onChange={(e) => setLocation(e.target.value)}
                              startAdornment={
                                <InputAdornment position="start">
                                  <MdOutlineSearch />
                                </InputAdornment>
                              }
                            />
                          </FormControl>
                        </div>
                        <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 ">
                          <FormControl fullWidth>
                            <InputLabel id="note-outlined-label">
                              Note
                            </InputLabel>
                            <Select
                              labelId="note-outlined-label"
                              id="note-outlined"
                              value={note}
                              onChange={(e) => {
                                setNote(e.target.value as string);
                              }}
                              label="Note"
                            >
                              {noteList.map((note) => {
                                return (
                                  <MenuItem key={note} value={note}>
                                    {note}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="actions clearfix">
          <ul role="menu" aria-label="Pagination">
            <li aria-hidden="false" aria-disabled="false">
              <span onClick={() => goToStep(2)}>Back</span>
            </li>
            <li
              className={"d-flex flex-row"}
              aria-hidden="false"
              aria-disabled="false"
            >
              <span
                className={"me-2"}
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </span>
              <span
                onClick={() => {
                  HttpClient.performPost<any>(`/api/v1/waiting/${spaceId}`, {
                    date: moment(new Date(selectedDate)).format("YYYY-MM-DD"), //.toLocaleString().split(",")[0].split("/").reverse().join("-"),
                    attendeeId:
                      mainUserProfile?.clinic_patient_id ??
                      searchParams.get("pid") ??
                      "",
                    ownerId:
                      utils.getDoctorProfileForAppointmentBooking().id ||
                      receptionistProfile.doctorId,
                    location: location,
                    note: note,
                  })
                    .then((response) => {
                      console.log("response", response);
                      if (!response?.body?.status?.success) {
                        setSnackbar({
                          message: response?.body?.status?.message,
                          severity: "error",
                        });
                      } else {
                        setSnackbar({
                          message: "Added to waiting list successfully!",
                          severity: "success",
                        });
                        navigate("/today-appointment", { replace: false });
                      }
                    })
                    .catch((e) => {
                      console.log(e);
                    })
                    .finally(() => {
                      console.log("finally!");
                    });
                }}
              >
                Confirm
              </span>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const Step6 = () => {
    const { goToStep } = useWizard();
    const navigate = useNavigate();

    return (
      <div className="success_step">
        <div className="content clearfix">
          <section>
            <div className="container py-5">
              <div className="row justify-content-center">
                <div className="col-lg-6 text-center">
                  {/* Success Icon */}
                  <div className="mb-4">
                    <div
                      className="rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center"
                      style={{ width: "80px", height: "80px" }}
                    >
                      <span className="text-success fs-2">✓</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h4 className="fw-bold">Appointment booked successfully!</h4>

                  {/* Subtitle */}
                  <p className="text-muted mt-2">
                    Appointment for <strong>{mainUserProfile?.name}</strong>{" "}
                    with{" "}
                    <strong>
                      {utils.getDoctorProfileForAppointmentBooking().name}
                    </strong>
                    <br />
                    on{" "}
                    <strong>
                      {moment(new Date(selectedDate)).format("DD MMM YYYY")} at{" "}
                      {selectedTimeSlot?.time}
                    </strong>
                  </p>

                  {/* Session Summary Card */}
                  <div className="card mt-4 shadow-sm border-0 bg-light">
                    <div className="card-body text-start">
                      <small className="text-uppercase text-muted">
                        Appointments booked in this session
                      </small>

                      <div className=" mt-3 w-100">
                        {sessionBookings.map((booking, index) => (
                          <div
                            key={index}
                            className="mb-2 d-flex align-items-center border-bottom w-100"
                          >
                            {" "}
                            <span
                              className="badge bg-success rounded-circle me-3"
                              style={{
                                width: "28px",
                                height: "28px",
                                lineHeight: "28px",
                                padding: 0,
                              }}
                            >
                              {index + 1}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                              <div>
                                {moment(booking.date).format("Do MMM YYYY")}
                              </div>

                              <span className="text-muted">·</span>

                              <div>
                                {booking.endTime
                                  ? `${booking.startTime} - ${booking.endTime}`
                                  : booking.startTime}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="mt-4 d-flex justify-content-center gap-3">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        // reset scheduling state
                        setSelectedDate("");
                        setSelectedTimeSlot(undefined);
                        setSelectedEndTimeSlot(undefined);
                        setSelectedTherapy(null);
                        setTherapyBookingNote("");

                        goToStep(1); // Step2
                      }}
                    >
                      Book Another Appointment
                    </button>

                    <button
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        navigate("/today-appointment", { replace: false })
                      }
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  };

  return (
    <PageWrapper>
      <div className="main_content">
        <div className="container-fluid">
          <div className="tab_wrapper">
            <div style={{ marginTop: "3em" }} className={classes.root}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="simple tabs example"
                indicatorColor="primary"
              >
                <Tab
                  className="custom_tab_header"
                  label="Appointment"
                  {...a11yProps(0)}
                />
                <Tab
                  className="custom_tab_header"
                  label="Reports"
                  {...a11yProps(1)}
                />
              </Tabs>
              <div className="tab_body_block">
                <div
                  className="custom_tab_content tab-content"
                  id="pills-tabContent"
                >
                  <TabPanel value={value} index={0}>
                    <div className="tab-pane fade show active" id="appointment">
                      <div className="appoinment_step_block">
                        <div className="appointment_step" id="appointment_step">
                          <Wizard startIndex={0}>
                            <Step1 />
                            <Step2 />
                            <Step3 />
                            <Step4 />
                            <Step5 />
                            <Step6 />
                          </Wizard>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                  <TabPanel value={value} index={1}>
                    <Reports />
                  </TabPanel>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PatientRelationDialog
        open={showPatientRelationDialog}
        doctorName={pendingDoctorName}
        patientRelationType={patientRelationType}
        onRelationTypeChange={setPatientRelationType}
        onConfirm={() => {
          setShowPatientRelationDialog(false);
          if (pendingNewPatientSubmit) {
            pendingNewPatientSubmit(patientRelationType); // 👈 pass current value
            setPendingNewPatientSubmit(null);
          }
        }}
        onCancel={() => {
          setShowPatientRelationDialog(false);
          setPendingNewPatientSubmit(null);
        }}
      />
    </PageWrapper>
  );
}
